import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isCountryCode, type CountryCode } from "@/lib/countries";

const STORAGE_KEY = "tirevault_country";

interface JurisdictionContextValue {
  country: CountryCode;
  setCountry: (c: CountryCode) => void;
  isLoading: boolean;
  source: "user" | "stored" | "geo" | "default";
}

const JurisdictionContext = createContext<JurisdictionContextValue | undefined>(undefined);

const UI_TO_COUNTRY: Record<string, CountryCode> = {
  sv: "sv",
  no: "no",
  da: "da",
  fi: "fi",
  is: "is",
};

async function detectCountryFromIp(): Promise<CountryCode | null> {
  try {
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 2500);
    const res = await fetch("https://ipapi.co/country/", { signal: ctrl.signal });
    clearTimeout(timeout);
    if (!res.ok) return null;
    const code = (await res.text()).trim().toLowerCase();
    const map: Record<string, CountryCode> = {
      se: "sv",
      no: "no",
      dk: "da",
      fi: "fi",
      is: "is",
    };
    return map[code] ?? null;
  } catch {
    return null;
  }
}

export const JurisdictionProvider = ({ children }: { children: ReactNode }) => {
  const [country, setCountryState] = useState<CountryCode>(() => {
    if (typeof window === "undefined") return "sv";
    const stored = localStorage.getItem(STORAGE_KEY);
    return isCountryCode(stored) ? stored : "sv";
  });
  const [isLoading, setIsLoading] = useState(true);
  const [source, setSource] = useState<JurisdictionContextValue["source"]>("default");

  const setCountry = (c: CountryCode) => {
    setCountryState(c);
    setSource("user");
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, c);
    }
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // 1. Logged-in user → company.country wins
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data } = await supabase.rpc("get_user_country", { _user_id: user.id });
          if (!cancelled && typeof data === "string" && isCountryCode(data)) {
            setCountryState(data);
            setSource("user");
            setIsLoading(false);
            return;
          }
        }
      } catch {
        /* ignore */
      }

      // 2. Stored value
      const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
      if (isCountryCode(stored)) {
        if (!cancelled) {
          setCountryState(stored);
          setSource("stored");
          setIsLoading(false);
        }
        return;
      }

      // 3. Geo-IP
      const geo = await detectCountryFromIp();
      if (!cancelled && geo) {
        setCountryState(geo);
        setSource("geo");
        if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, geo);
        setIsLoading(false);
        return;
      }

      // 4. UI language fallback
      const lang = typeof navigator !== "undefined" ? navigator.language?.slice(0, 2) : "sv";
      const fromLang = lang ? UI_TO_COUNTRY[lang] : undefined;
      if (!cancelled) {
        setCountryState(fromLang ?? "sv");
        setSource("default");
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <JurisdictionContext.Provider value={{ country, setCountry, isLoading, source }}>
      {children}
    </JurisdictionContext.Provider>
  );
};

export const useJurisdiction = () => {
  const ctx = useContext(JurisdictionContext);
  if (!ctx) throw new Error("useJurisdiction must be used inside JurisdictionProvider");
  return ctx;
};
